import { useEffect, type ReactNode } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { api } from '../api/client'
import type { StaffRole } from '../api/types'
import { useAuth } from '../auth/AuthContext'

interface NavItem {
  to: string
  label: string
  glyph: string
  /** Who sees it; admin only unless listed. */
  roles?: StaffRole[]
  /** Restricted to the root `admin` account, like the endpoint behind it. */
  rootOnly?: boolean
}

const SECTIONS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Overview',
    items: [{ to: '/dashboard', label: 'Dashboard', glyph: '◫' }],
  },
  {
    title: 'Deliveries',
    items: [
      { to: '/deliveries/today', label: 'Today', glyph: '◷', roles: ['admin', 'coordinator'] },
      { to: '/deliveries/inbox', label: 'Inbox', glyph: '▥', roles: ['admin', 'coordinator'] },
      { to: '/deliveries/plan', label: 'Plan a day', glyph: '▦', roles: ['admin', 'coordinator'] },
      { to: '/deliveries/analytics', label: 'Performance', glyph: '◔', roles: ['admin', 'coordinator'] },
      { to: '/deliveries/demo', label: 'Demo', glyph: '◌', roles: ['admin', 'coordinator'] },
      // Admins reach the same page as Explore › Invoices.
      { to: '/invoices', label: 'Lookup', glyph: '▤', roles: ['coordinator'] },
    ],
  },
  {
    title: 'Pickups',
    items: [
      { to: '/pickups/live', label: 'Live', glyph: '◷' },
      { to: '/pickups/analytics', label: 'Performance', glyph: '◔' },
    ],
  },
  {
    title: 'Explore',
    items: [
      { to: '/invoices', label: 'Invoices', glyph: '▤' },
      { to: '/products', label: 'Products', glyph: '◰' },
      { to: '/customers', label: 'Customers', glyph: '◎' },
    ],
  },
  {
    title: 'Payroll',
    items: [{ to: '/commission', label: 'Commission', glyph: '◇' }],
  },
  {
    title: 'Team',
    items: [
      { to: '/sales-team', label: 'Sales team', glyph: '◑' },
      { to: '/staff', label: 'Staff', glyph: '◍' },
      { to: '/app-versions', label: 'App versions', glyph: '⇡' },
      { to: '/usage', label: 'App usage', glyph: '◈', rootOnly: true },
    ],
  },
  {
    title: 'System',
    items: [{ to: '/system', label: 'Status', glyph: '◉' }],
  },
]

/**
 * A route template for usage analytics — one screen per page, not one per
 * invoice or customer. The last segment of a detail page is its record.
 */
function screenOf(pathname: string): string {
  return pathname
    .replace(/^\/(invoices|customers|sales-team)\/[^/]+$/, '/$1/:id')
    .replace(/^\/products\/(?!uncatalogued$)[^/]+$/, '/products/:sku')
}

export function Layout() {
  const { session, isAdmin, rootAdmin, signOut } = useAuth()
  const { pathname } = useLocation()

  // Same fire-and-forget screen view the phone app sends, so the usage page
  // can compare the two clients like for like.
  useEffect(() => {
    api.post('/usage/screen-view', { route: screenOf(pathname) }).catch(() => undefined)
  }, [pathname])

  const initials = `${session?.firstName?.[0] ?? ''}${session?.lastName?.[0] ?? ''}`.toUpperCase()
  const role = session?.role

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="mark">Fulfil</div>
          <div className="sub">{isAdmin ? 'Admin' : 'Deliveries'}</div>
        </div>

        <nav className="sidebar-nav">
          {SECTIONS.map((section) => {
            const items = section.items.filter(
              (item) =>
                role !== undefined &&
                (item.roles ?? ['admin']).includes(role) &&
                (!item.rootOnly || rootAdmin),
            )
            if (!items.length) return null
            return (
              <div key={section.title}>
                <div className="nav-section">{section.title}</div>
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                  >
                    <span className="glyph">{item.glyph}</span>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )
          })}
        </nav>

        <div className="sidebar-foot">
          <div className="avatar">{initials || '?'}</div>
          <div className="who">
            <div className="name">
              {session?.firstName} {session?.lastName}
            </div>
            <div className="role">{rootAdmin ? 'Root admin' : isAdmin ? 'Admin' : 'Coordinator'}</div>
          </div>
          <button className="btn ghost sm" onClick={signOut} title="Sign out">
            ⎋
          </button>
        </div>
      </aside>

      <div className="main">
        <Outlet />
      </div>
    </div>
  )
}

export function PageHeader({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: ReactNode
  children?: ReactNode
}) {
  return (
    <header className="topbar">
      <div className="titles">
        <h1>{title}</h1>
        {subtitle ? <div className="subtitle">{subtitle}</div> : null}
      </div>
      {children}
    </header>
  )
}
